import { defineComponent as _defineComponent } from 'vue'
import { normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, vModelText as _vModelText, withKeys as _withKeys, withDirectives as _withDirectives, createTextVNode as _createTextVNode, resolveDirective as _resolveDirective, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createVNode as _createVNode, vShow as _vShow } from "vue"

const _hoisted_1 = { class: "row1 mb8" }
const _hoisted_2 = { class: "date ib mr10" }
const _hoisted_3 = ["value"]
const _hoisted_4 = { class: "time ib" }
const _hoisted_5 = ["readonly"]
const _hoisted_6 = ["disabled"]
const _hoisted_7 = ["disabled"]
const _hoisted_8 = { class: "ta-right mt4 ml4" }
const _hoisted_9 = ["disabled"]
const _hoisted_10 = { class: "row2 mb8" }
const _hoisted_11 = { class: "location ib" }
const _hoisted_12 = ["value"]
const _hoisted_13 = { class: "row3 mb8" }
const _hoisted_14 = { key: 0 }
const _hoisted_15 = { class: "tags" }
const _hoisted_16 = { class: "row4" }
const _hoisted_17 = { class: "three-axis-sensor ib" }
const _hoisted_18 = { class: "speed-meter" }
const _hoisted_19 = { class: "speed text-primary mr4" }
const _hoisted_20 = { class: "row5 mt8" }

import ThreeAxisSensorChart from 'src/components/lib/ThreeAxisSensorChart/index.vue';
import {
  computed,
  nextTick,
  reactive,
  watch,
} from 'vue';
import type { SensorData } from 'src/models/mtx';
import type { GIMovie, GIMovieGeoIndex } from 'src/models/geoItem';
import { dtFormat, ensureDate } from 'src/lib/dateHelper';

interface MovieMetaInfoAreaState {
  isEditingTime: boolean;
  showSensorChart: boolean;
  timeDisp: string;
  sensorData: SensorData[];
}

interface Props {
  movie: GIMovie | null;
  movieGeoIndex: GIMovieGeoIndex | null;
  movieCurrentDt: Date | null;
  enableSensorChart: boolean;
  enableTimeSeek: boolean;
  viewMode: 'full' | 'comp';
  cornerLabelOpacity?: number;
  locationDisp?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  props: {
    movie: {},
    movieGeoIndex: {},
    movieCurrentDt: {},
    enableSensorChart: { type: Boolean },
    enableTimeSeek: { type: Boolean },
    viewMode: {},
    cornerLabelOpacity: { default: 1.0 },
    locationDisp: { default: '' }
  },
  emits: ["start-time-edit", "cancel-time-edit", "move-to-time", "edit-movie-tags", "try-show-new-sensor-chart"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const state = reactive<MovieMetaInfoAreaState>({
  isEditingTime: false,
  showSensorChart: false,
  timeDisp: '',
  sensorData: [],
});
const dateDisp = computed<string>(() => {
  if (!props.movieGeoIndex) return '';

  return dtFormat(props.movieGeoIndex.ts, 'yyyy/mm/dd');
});
const velocityDisp = computed<string>(() => {
  if (!props.movieGeoIndex) return '---';

  const velo = parseInt(props.movieGeoIndex.velocity ?? '');
  return isNaN(velo) ? '---' : velo.toString();
});

watch(() => props.movieCurrentDt, () => {
  if (!props.movieCurrentDt) {
    state.timeDisp = '';
    return;
  }
  state.timeDisp = dtFormat(props.movieCurrentDt, 'HH:MM:SS');
});

// computed:
const isViewModeFull = computed(() => {
  return props.viewMode === 'full';
});
const isViewModeComp = computed(() => {
  return props.viewMode === 'comp';
});
const isValidTimeDispValue = computed(() => {
  const arr = state.timeDisp.split(':')
    .filter(e => e.match(/^\d\d$/)).map(e => parseInt(e));
  if (arr.length !== 3) return false;

  const hour = arr[0];
  const minute = arr[1];
  const second = arr[2];
  if (
    0 <= hour && hour < 24 &&
    0 <= minute && minute < 60 &&
    0 <= second && second < 60
  ) {
    return true;
  }
  return false;
});
const movieTags = computed(() => {
  if (!props.movie) return [];

  return props.movie.tags;
});
const canCopyLink = computed(() => {
  return !!window.navigator.clipboard;
});
const copyLinkTooltip = computed(() => {
  return canCopyLink.value ? '' : 'http環境ではこの機能は使えません';
});

// methods:
const setSensorData = (sensorData: SensorData[]) => {
  state.sensorData = sensorData;
};
const setAllUIStateToDefault = () => {
  state.isEditingTime = false;
  state.showSensorChart = false;
  setSensorData([]);
};

let timeDispOrig = '';
const startTimeEdit = () => {
  timeDispOrig = state.timeDisp;
  state.isEditingTime = true;
  emit('start-time-edit');
};
const cancelTimeEdit = () => {
  state.timeDisp = timeDispOrig;
  state.isEditingTime = false;
  emit('cancel-time-edit');
};
const moveToEditedTime = () => {
  if (!state.isEditingTime || !isValidTimeDispValue.value) return;

  const timeDisp = state.timeDisp;
  const dt = ensureDate(`${dateDisp.value} ${timeDisp}`);
  if (!dt) return;

  emit('move-to-time', { dt, dateDisp: dateDisp.value, timeDisp });
  if (document.activeElement) {
    // time-inputにフォーカスが残ったままになるのが嫌なので
    (document.activeElement as HTMLElement).blur();
  }
  nextTick(() => {
    state.isEditingTime = false;
  });
};
const editMovieTags = () => {
  emit('edit-movie-tags');
};
const openSensorChart = () => {
  state.showSensorChart = true;
  if (state.sensorData.length === 0) {
    emit('try-show-new-sensor-chart');
  }
};
const closeSensorChart = () => {
  state.showSensorChart = false;
};
const onSensorChartClick = (dt: Date) => {
  if (!dt) return;

  dt = new Date(dt);
  const dateDisp = dtFormat(dt, 'yyyy/mm/dd');
  const timeDisp = dtFormat(dt, 'HH:MM:SS');
  emit('move-to-time', { dt, dateDisp, timeDisp });
};
const copyLink = () => {
  const dt = ensureDate(`${dateDisp.value} ${state.timeDisp}`);
  if (!props.movie?.ts || !dt) return;

  const offset = dt.getTime() - props.movie.ts.getTime();
  const link = `${location.origin}/?movie_id=${props.movie.id}&start_offset=${offset}`;
  window.navigator.clipboard.writeText(link);
};

__expose({ setAllUIStateToDefault, setSensorData });

return (_ctx: any,_cache: any) => {
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["movie-meta-info-area", {
      'full-mode': isViewModeFull.value,
      'compare-mode': isViewModeComp.value,
    }])
  }, [
    (isViewModeComp.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "corner-triangle-label",
          style: _normalizeStyle({opacity: __props.cornerLabelOpacity})
        }, null, 4))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _cache[1] || (_cache[1] = _createElementVNode("label", { class: "form-control-label mr4" }, "日付", -1)),
        _createElementVNode("input", {
          type: "text",
          class: "form-control ib",
          value: dateDisp.value,
          readonly: ""
        }, null, 8, _hoisted_3)
      ]),
      _createElementVNode("div", _hoisted_4, [
        _cache[5] || (_cache[5] = _createElementVNode("label", { class: "form-control-label mr4" }, "時刻", -1)),
        _withDirectives(_createElementVNode("input", {
          type: "text",
          class: "form-control ib mr4",
          maxlength: "8",
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((state.timeDisp) = $event)),
          readonly: !state.isEditingTime,
          onKeyup: _withKeys(moveToEditedTime, ["enter"])
        }, null, 40, _hoisted_5), [
          [_vModelText, state.timeDisp]
        ]),
        (!state.isEditingTime)
          ? (_openBlock(), _createElementBlock("button", {
              class: "btn btn-primary btn-sm",
              key: "startTimeEdit",
              disabled: !__props.enableTimeSeek,
              onClick: startTimeEdit
            }, [...(_cache[2] || (_cache[2] = [
              _createElementVNode("span", null, "時刻指定", -1)
            ]))], 8, _hoisted_6))
          : _createCommentVNode("", true),
        (state.isEditingTime)
          ? (_openBlock(), _createElementBlock("button", {
              class: "btn btn-primary btn-sm",
              key: "moveToEditedTime",
              onClick: moveToEditedTime,
              disabled: !isValidTimeDispValue.value
            }, [...(_cache[3] || (_cache[3] = [
              _createElementVNode("span", null, "移動", -1)
            ]))], 8, _hoisted_7))
          : _createCommentVNode("", true),
        (state.isEditingTime)
          ? (_openBlock(), _createElementBlock("button", {
              class: "btn btn-secondary btn-sm",
              key: "cancelTimeEdit",
              onClick: cancelTimeEdit
            }, [...(_cache[4] || (_cache[4] = [
              _createElementVNode("span", null, "キャンセル", -1)
            ]))]))
          : _createCommentVNode("", true)
      ]),
      _cache[7] || (_cache[7] = _createElementVNode("div", { class: "spacer" }, null, -1)),
      _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_8, [
        (!state.isEditingTime)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              class: "btn btn-secondary btn-sm",
              disabled: !canCopyLink.value,
              onClick: copyLink
            }, [...(_cache[6] || (_cache[6] = [
              _createElementVNode("span", null, [
                _createElementVNode("i", { class: "far fa-copy" }),
                _createTextVNode("リンクをコピー")
              ], -1)
            ]))], 8, _hoisted_9))
          : _createCommentVNode("", true)
      ])), [
        [_directive_tooltip, copyLinkTooltip.value]
      ])
    ]),
    _createElementVNode("div", _hoisted_10, [
      _createElementVNode("div", _hoisted_11, [
        _cache[8] || (_cache[8] = _createElementVNode("label", { class: "form-control-label mr4" }, "場所", -1)),
        _createElementVNode("input", {
          type: "text",
          class: "form-control ib",
          value: __props.locationDisp,
          readonly: ""
        }, null, 8, _hoisted_12)
      ])
    ]),
    _createElementVNode("div", _hoisted_13, [
      _createElementVNode("button", {
        class: "btn btn-info btn-sm mr4 edit-tag-btn",
        onClick: editMovieTags
      }, [
        _cache[9] || (_cache[9] = _createElementVNode("i", { class: "fas fa-tag" }, null, -1)),
        (movieTags.value.length === 0)
          ? (_openBlock(), _createElementBlock("span", _hoisted_14, "タグ編集"))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_15, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(movieTags.value, (e, i) => {
          return (_openBlock(), _createElementBlock("span", {
            class: _normalizeClass(["tag-badge mr4 mb4", 'tag-badge-' + e.tagType1]),
            key: i
          }, _toDisplayString(e.tagName), 3))
        }), 128))
      ])
    ]),
    _createElementVNode("div", _hoisted_16, [
      _createElementVNode("div", _hoisted_17, [
        (__props.enableSensorChart)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              (!state.showSensorChart)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    class: "btn btn-secondary btn-sm",
                    onClick: openSensorChart
                  }, " 三軸センサー表示 "))
                : (_openBlock(), _createElementBlock("button", {
                    key: 1,
                    class: "btn btn-secondary btn-sm",
                    onClick: closeSensorChart
                  }, " 三軸センサー非表示 "))
            ], 64))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_18, [
        _createElementVNode("span", _hoisted_19, _toDisplayString(velocityDisp.value), 1),
        _cache[10] || (_cache[10] = _createElementVNode("span", { class: "unit" }, "km/h", -1))
      ])
    ]),
    _withDirectives(_createElementVNode("div", _hoisted_20, [
      _createVNode(ThreeAxisSensorChart, {
        "sensor-data": state.sensorData,
        onClick: onSensorChartClick
      }, null, 8, ["sensor-data"])
    ], 512), [
      [_vShow, state.showSensorChart]
    ])
  ], 2))
}
}

})